import { Component, EventEmitter, Inject, Output } from '@angular/core';
import {
    AgentService,
    ApiService,
    BrandingModeService,
    FeatureService,
    FrontdoorV3Service,
    IdentityPreferencesService,
    INFRASTRUCTURE_URLS,
    MenuService,
    URLS,
    ZITI_URLS,
    ZITI_BETA_LIST_ROUTES,
} from '@netfoundry-ui/shared/services';
import { NavigationEnd, Router } from '@angular/router';
import { ConfirmComponent } from '@netfoundry-ui/ui/confirm';
import { defer, delay } from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import { AuthorizationService, LoginService } from '@netfoundry-ui/shared/authorization';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import {
    BILLING_NAVIGATOR,
    NETWORKS_NAVIGATOR,
    ORGANIZATION_NAVIGATOR,
    SUPPORT_NAVIGATOR,
    ZAC_NAVIGATOR,
    SUPPORT_OPERATIONS_NAVIGATOR,
    FRONTDOOR_NAVIGATOR,
    FD_NAVIGATOR,
    CUSTOMERS_NAVIGATOR
} from '@netfoundry-ui/feature/navigator';
import { AccountService } from '@netfoundry-ui/feature/shared-services';
import { ZitiControllerService } from "@netfoundry-ui/shared/apiv2";
import { ZITI_DOMAIN_CONTROLLER } from "ziti-console-lib";

import { first, isEmpty, last } from 'lodash';
import { routeBreadcrumb } from './route-breadcrumb';

@Component({
    selector: 'netfoundry-ui-app-switcher',
    templateUrl: './app-switcher.component.html',
    styleUrls: ['./app-switcher.component.scss'],
    standalone: false
})
export class AppSwitcherComponent {
    @Output() showGrowlersEvent: EventEmitter<boolean> = new EventEmitter();
    showAppMenu = false;
    selectedApp = '';
    popup = '';
    confirmDialog: any;
    area = '';
    navIconClass = '';
    subscription = new Subscription();
    billingStatusInvalid = false;
    username: any = '';
    photo: any;
    isV2;
    /** True when the current tenant has a v1/v2 frontdoor. The legacy Frontdoor menu entry shows only then. */
    hasLegacyFrontdoor = false;
    /** True when the user can reach a v3 frontdoor. The v3 Frontdoor menu entry shows only then. */
    hasV3Frontdoors = false;
    SUPPORT_OPERATIONS = URLS.SUPPORT_OPERATIONS;
    FRONTDOOR = URLS.FRONTDOOR;
    CUSTOMERS = URLS.CUSTOMERS;
    enterpriseBilling = false;
    currentOrganization: any = {};
    currentNetwork: any = {};
    currentNetworkGroup: any = {};
    currentAccount: any = {};
    currentTenant: any = {};
    currentShare: any = {}
    currentCustomer: any = {};
    currentNetworkGroups = [];
    cloudZitiNavigators = [
        ZAC_NAVIGATOR,
        ORGANIZATION_NAVIGATOR,
        BILLING_NAVIGATOR,
        SUPPORT_NAVIGATOR,
        FRONTDOOR_NAVIGATOR,
        FD_NAVIGATOR,
        SUPPORT_OPERATIONS_NAVIGATOR,
        CUSTOMERS_NAVIGATOR
    ];
    mopNavigators = [NETWORKS_NAVIGATOR, ORGANIZATION_NAVIGATOR, SUPPORT_NAVIGATOR, SUPPORT_OPERATIONS_NAVIGATOR];

    url = '';
    pageNamePath: any = undefined;

    appNameMap = {
        infrastructure: 'Network',
        networks: 'Network',
        billing: 'Billing',
        organization: 'Organization',
        support: 'Support',
        support_operations: 'Operations',
        frontdoor: 'Frontdoor',
        fd: 'Frontdoor',
        customers: 'Customers',
        on_prem: 'Self-Hosted'
    };

    constructor(
        private apiService: ApiService,
        private dialogForm: MatDialog,
        private agentService: AgentService,
        private loginService: LoginService,
        private menuService: MenuService,
        private router: Router,
        private featureService: FeatureService,
        private accountService: AccountService,
        public authService: AuthorizationService,
        private brandingService: BrandingModeService,
        private identityPreferences: IdentityPreferencesService,
        private frontdoorV3Service: FrontdoorV3Service,
        @Inject(ZITI_DOMAIN_CONTROLLER) private controllerService: ZitiControllerService,
    ) { }

    /** Current theme preference for the profile menu's quick switch ('auto' when unset). */
    get currentTheme(): string {
        const pref = this.identityPreferences.getPreference('darkModeEnabled');
        return pref === undefined || pref === null || pref === '' ? 'auto' : pref.toString();
    }

    /** Apply + persist a theme picked from the profile menu (same path as User Settings). */
    setTheme(value: string): void {
        this.featureService.darkModeEnabled = value;
        this.brandingService.updateDarkMode();
    }

    /** Beta-features flag for the profile menu's quick toggle (support-only, enforced in the service). */
    get betaEnabled(): boolean {
        return !!this.featureService.betaFeaturesEnabled;
    }

    toggleBeta(): void {
        const enabling = !this.betaEnabled;
        // Capture the current route before flipping — if we're on a page that has a beta/legacy
        // pair (the dashboard or the identities list), follow the toggle to the counterpart.
        const onDashboard = this.isOnDashboardRoute();
        const onIdentities = this.isOnIdentitiesRoute();
        const betaListPair = this.betaListPairForCurrentRoute();
        this.featureService.betaFeaturesEnabled = enabling;
        if (onDashboard) {
            this.router.navigate([enabling ? INFRASTRUCTURE_URLS.OVERVIEW : INFRASTRUCTURE_URLS.DASHBOARD]);
        } else if (onIdentities) {
            this.router.navigate([enabling ? ZITI_URLS.ZITI_IDENTITIES_LIST : ZITI_URLS.ZITI_IDENTITIES]);
        } else if (betaListPair) {
            this.router.navigate([enabling ? betaListPair.beta : betaListPair.legacy]);
        }
    }

    /** True when the current route is the redesigned Overview or the legacy Dashboard. */
    private isOnDashboardRoute(): boolean {
        const url = (this.router.url || '').split('?')[0].replace(/\/+$/, '');
        return url === INFRASTRUCTURE_URLS.OVERVIEW || url === INFRASTRUCTURE_URLS.DASHBOARD;
    }

    /** True when the current route is the redesigned or the legacy identities list. */
    private isOnIdentitiesRoute(): boolean {
        const url = (this.router.url || '').split('?')[0].replace(/\/+$/, '');
        return url === ZITI_URLS.ZITI_IDENTITIES_LIST || url === ZITI_URLS.ZITI_IDENTITIES;
    }

    /** The legacy/beta URL pair when the current route is one of the policy or posture check lists. */
    private betaListPairForCurrentRoute(): { legacy: string; beta: string } | undefined {
        const url = (this.router.url || '').split('?')[0].replace(/\/+$/, '');
        const legacy = Object.keys(ZITI_BETA_LIST_ROUTES).find((l) => l === url || ZITI_BETA_LIST_ROUTES[l] === url);
        return legacy && legacy !== ZITI_URLS.ZITI_IDENTITIES ? { legacy, beta: ZITI_BETA_LIST_ROUTES[legacy] } : undefined;
    }

    /** True when the current route is Traffic Analysis (one URL serves both the beta and legacy views). */
    private isOnTrafficAnalysisRoute(): boolean {
        const url = (this.router.url || '').split('?')[0].replace(/\/+$/, '');
        return url === URLS.TRAFFIC_ANALYSIS;
    }

    /**
     * Traffic Analysis has a beta and a legacy view at the same URL, chosen by a canMatch guard.
     * When the beta flag actually flips (betaFeaturesChanged fires after the preference lands), re-enter
     * the route so the guard picks the other view. A unique param forces the navigation through, and
     * skipLocationChange keeps the address bar and history unchanged - no page reload, no URL change.
     */
    private followBetaChangeOnTrafficAnalysis(): void {
        if (!this.isOnTrafficAnalysisRoute()) {
            return;
        }
        this.router.navigate([URLS.TRAFFIC_ANALYSIS], { queryParams: { _b: Date.now() }, skipLocationChange: true });
    }

    ngOnInit() {
        this.username = localStorage.getItem('profile_nick');
        this.photo = localStorage.getItem('profile_picture');
        this.checkV3Frontdoors();
        this.subscription.add(
            this.apiService.billingStatusInvalid.subscribe((status: boolean) => {
                this.billingStatusInvalid = status;
            })
        );

        this.subscription.add(
            this.accountService.currentAccount.subscribe((account) => {
                this.currentAccount = account;
                this.enterpriseBilling = account?.billingMode === 'Enterprise';
            })
        );
        this.subscription.add(
            this.apiService.currentNetwork.subscribe((network) => {
                this.currentNetwork = network;
            })
        );
        this.subscription.add(
          this.apiService.currentOrganization.subscribe((org) => {
            this.currentOrganization = org;
          })
        );
        this.subscription.add(
            this.apiService.currentCustomer.subscribe((customer) => {
                this.currentCustomer = customer;
            })
        );
        this.subscription.add(
            this.featureService.betaFeaturesChanged.subscribe(() => this.followBetaChangeOnTrafficAnalysis())
        );
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.url = event.url.split('?')[0];
                this.updateBreadcrumbPath();
                this.updateSelectedApp();
            }
        });
        this.subscription.add(
            this.apiService.currentNetworkGroup.subscribe((networkGroup) => {
                this.currentNetworkGroup = networkGroup;
            })
        );
        // switchMap drops the answer for a tenant the user has already switched away from.
        this.subscription.add(
            this.apiService.currentTenant
                .pipe(
                    tap((tenant) => (this.currentTenant = tenant)),
                    switchMap((tenant) => this.legacyFrontdoor(tenant?.id))
                )
                .subscribe((frontdoor) => {
                    this.hasLegacyFrontdoor = !!frontdoor?.id;
                    this.isV2 = frontdoor?.version !== 1;
                })
        );
        this.url = this.router.url.split('?')[0];
        this.menuService.appChanged.subscribe((app) => {
            this.selectedApp = app;
        });
        defer(() => {
            this.updateSelectedApp();
            this.updateBreadcrumbPath();
        });
    }

    open(area: string) {
        if (area === 'Alerts') {
            this.showGrowlersEvent.emit();
            this.menuService.showAlerts();
        } else if (area === 'Profile') {
            if (this.popup === 'profile') {
                this.popup = '';
            } else {
                this.popup = 'profile';
            }
        } else if (area === 'Support') {
            this.router.navigate([URLS.GETTING_STARTED]);
            this.menuService.setArea('Support');
        } else if (area === 'Customers') {
            this.router.navigate([URLS.CUSTOMERS]);
            this.menuService.setArea('Customers');
        }
    }

    openAppMenu() {
        this.showAppMenu = !this.showAppMenu;
        if (this.showAppMenu) {
            this.checkV3Frontdoors();
        }
    }

    updateBreadcrumbPath() {
        // The tabs of the Users ribbon, and the role template form under them, show the left nav item, as the FD ribbons do.
        const usersRibbon = [URLS.USERS, URLS.USER_ROLES, URLS.ORGANIZATION_ROLES, URLS.MANAGE_INVITATIONS, URLS.ROLE_TEMPLATES];
        const path = this.router?.url?.split(/[?#]/)[0] || '';
        if (usersRibbon.some((route) => path === route || path.startsWith(route + '/'))) {
            this.pageNamePath = 'Users';
            return;
        }
        const splitUrl = this.router?.url?.split('/');
        let lastRouteElement = last(splitUrl);
        if (lastRouteElement) {
            // The fragment is cut as well as the query. Neither names the page, and the certificate
            // enrollment link carries its JWT in the fragment — left on, the whole credential is
            // printed across the header of a page anybody with the link can open.
            lastRouteElement = first(first(lastRouteElement.split('?'))?.split('#'));
            // A route can name itself in `data.breadcrumb`, where the url segment reads badly (`oidc-providers`).
            this.pageNamePath = routeBreadcrumb(this.router?.routerState?.snapshot?.root) ?? lastRouteElement;
        }
    }

    updateSelectedApp() {
        // The two sets differ in which items carry `betaListSwitch`, so a narrower inferred type would reject the swap.
        let navigators: any[] = this.mopNavigators;
        if (this.featureService.isCloudZiti) {
            navigators = this.cloudZitiNavigators;
        }
        let selectedApp;
        let selectedNavItem;
        let partialMatchNavItem;
        let partialMatchApp;
        navigators.forEach((navigator: any) => {
            navigator.groups.forEach((group: any) => {
                group.menuItems.forEach((menuItem: any) => {
                    if (!menuItem.selectedRoutes) {
                        return;
                    }
                    menuItem.selectedRoutes.forEach((route: any) => {
                        const urlSegments = this.url.split('/').filter((segment: string) => {
                            return !isEmpty(segment);
                        });
                        const routeSegments = route.split('/').filter((segment: string) => {
                            return !isEmpty(segment);
                        });

                        if (this.url === route) {
                            selectedApp = navigator.app;
                            selectedNavItem = menuItem;
                            return;
                        }

                        const isPrefix = urlSegments.length >= routeSegments.length &&
                            routeSegments.every((seg, i) => seg === urlSegments[i]);
                        if (isPrefix) {
                            partialMatchNavItem = menuItem;
                            partialMatchApp = navigator.app;
                        }
                    });
                    if (selectedApp || selectedNavItem) {
                        return;
                    }
                });
                if (selectedApp || selectedNavItem) {
                    return;
                }
            });
            if (selectedApp || selectedNavItem) {
                return;
            }
        });
        if (selectedApp || selectedNavItem) {
            this.menuService.setApp(selectedApp);
            this.menuService.setNavItem(selectedNavItem)
        } else if (partialMatchNavItem && partialMatchApp) {
            this.menuService.setApp(partialMatchApp);
            selectedNavItem = partialMatchNavItem;
        } else {
            this.menuService.setApp('mop');
        }
        if (!selectedNavItem) {
            selectedNavItem = this.menuService.DEFAULT_NAV_ITEM;
        }
        this.menuService.setNavItem(selectedNavItem);
    }

    appSelected(app: string, navRoute?: any) {
        switch (app) {
            case 'infrastructure':
                if (navRoute) {
                    this.router.navigate([navRoute]);
                } else {
                    this.router.navigate([this.featureService.dashboardRoute]);
                }
                this.menuService.setArea('ZAC');
                break;
            case 'networks':
                this.router.navigate([this.featureService.dashboardRoute]);
                this.menuService.setArea('ZAC');
                break;
            case 'billing':
                if (!this.currentAccount?.id) {
                    return;
                }
                if (this.enterpriseBilling) {
                    this.router.navigate([URLS.BILLING_PAYMENT_PROFILE]);

                } else {
                    this.router.navigate([URLS.BILLING_DASHBOARD]);
                }
                this.menuService.setArea('Billing');
                break;
            case 'organization':
                this.router.navigate([URLS.USERS]);
                this.menuService.setArea('Organization');
                break;
            case 'support_operations':
                this.router.navigate([URLS.SUPPORT_OPERATIONS]);
                this.menuService.setArea('Support_Operations');
                break;
            case 'support':
                this.menuService.setArea('Support');
                break;
            case 'on_prem':
                this.router.navigate([URLS.ON_PREM]);
                this.menuService.setArea('On_Prem');
                break;
            case 'mop':
                this.menuService.setArea('MOP');
                break;
            case 'fd':
                // The /fd route picks the landing page: the dashboard for beta users, TCP shares otherwise.
                this.router.navigate([URLS.FD]);
                this.menuService.setArea('FD');
                break;
            case 'frontdoor':
                if(this.isV2) {
                    this.router.navigate([URLS.FRONTDOOR_HTTP]);
                    this.menuService.setArea('Frontdoor');
                } else {
                    this.router.navigate([URLS.FRONTDOOR]);
                    this.menuService.setArea('Frontdoor');
                }
                break;
            case 'customers':
                this.router.navigate([URLS.CUSTOMERS]);
                this.menuService.setArea('Customers');
                break;
        }
        this.selectedApp = app;
        this.showAppMenu = false;
        defer(() => {
            if (!isEmpty(app)) {
                this.menuService.setApp(app);
            } else {
                this.menuService.setApp('mop');
            }
        });
    }

    hover(id: string) {
        this.popup = id;
    }

    toggleAppMenu() {
        const show = !this.showAppMenu;
        if (show) {
            this.checkV3Frontdoors();
        }
        defer(() => {
            this.showAppMenu = show;
        });
        //this.menuService.resetArea();
    }

    closeAppMenu() {
        this.showAppMenu = false;
    }

    confirmLogout() {
        const data = {
            title: 'Log out of your account',
            appendId: 'Logout',
            subtitle: 'To continue, please tap the "Log out" button.',
            icon: 'Logout',
            action: 'LOG OUT',
            hideCancelOnConfirm: false
        };
        this.confirmDialog = this.dialogForm.open(ConfirmComponent, {
            data: data,
            height: '225px',
            width: '600px',
            autoFocus: false,
        });
        this.confirmDialog.beforeClosed().subscribe((result: any) => {
            // if the result has a property loggingOut, rather than being just a boolean value, the user is being
            //  logged out of the console and we should close the dialog without continuing
            if (result === undefined || result === false) {
                this.confirmed(false);
            } else if (result['loggingOut'] === undefined) {
                if (this.area !== '') {
                    this.open(this.area);
                }
                this.confirmed(result);
            }
        });
    }

    confirmed(event: any) {
        if (event) {
            const data = {
                title: 'Logging Out',
                appendId: 'Logout',
                subtitle: 'Please wait while you are logged out of the system...',
                icon: 'Logout',
                hideCancelLink: true
            };
            this.confirmDialog = this.dialogForm.open(ConfirmComponent, {
                data: data,
                height: '225px',
                width: '600px',
                autoFocus: false,
            });
            this.loginService.willLogout();
            delay(() => {
                this.loginService.logout(false);
            }, 100);
        }
    }

    get networkAppTooltip() {
        let tooltip = '';
        if (!this.currentNetwork?.id) {
            tooltip = 'Must select or create a network';
        } else if (this.controllerService.loadingZitiController) {
            tooltip = 'Waiting to establish session with Ziti controller...';
        } else if (!this.controllerService.hasZitiSession) {
            tooltip = 'Unable to establish valid session with selected network...'
        }
        return tooltip;
    }

    goToNetworkGroupsPage(event: any) {
        this.router.navigate([INFRASTRUCTURE_URLS.NETWORK_GROUPS]);
    }

    goToOrgsPage(event: any) {
        this.router.navigate([URLS.USERS]);
    }


    goToNetworksPage(event: any) {
        this.router.navigate([INFRASTRUCTURE_URLS.NETWORKS]);
    }

    goToPage(event: any) {
        switch (this.selectedApp) {
            case 'networks':
                this.router.navigate([INFRASTRUCTURE_URLS.NETWORKS]);
                break;
            case 'organization':
                this.router.navigate([URLS.USERS]);
                break;
            case 'billing':
                this.router.navigate([URLS.BILLING_PAYMENT_PROFILE]);
                break;
            case 'support_operations':
                this.router.navigate([URLS.SUPPORT_OPERATIONS]);
                break;
            case 'frontdoor':
                this.router.navigate([URLS.FRONTDOOR]);
                break;
            case 'on-prem':
                this.router.navigate([URLS.ON_PREM]);
                break;
        }
    }

    goToNetworkDashboard(event: any) {
        this.router.navigate([this.featureService.dashboardRoute]);
    }

    get showOpperations() {
        return this.authService.isSupportUser() && this.authService.canListNetworks(this.currentNetworkGroup.id);
    }

    get showFrontdoor() {
        return;
    }


    get showCustomers() {
        return this.featureService.experimentalFeatures && this.authService.canListCustomers();
    }

    get showMore() {
        return this.showOpperations || this.showCustomers || this.showFrontdoor;
    }

    /** Reads the cached v3 frontdoor list. A cleared cache, after an update or a delete, fetches it again. */
    private checkV3Frontdoors() {
        this.subscription.add(this.frontdoorV3Service.isV3().subscribe((isV3) => (this.hasV3Frontdoors = isV3)));
    }

    /** The tenant's v1/v2 frontdoor account, or null when it has none. The service answers 403 for an org without one. */
    private legacyFrontdoor(tenantId?: string): Observable<any> {
        if (!tenantId) {
            return of(null);
        }
        return this.agentService.getAccountById(tenantId).pipe(catchError(() => of(null)));
    }
}
